// ─────────────────────────────────────────────────────────────────────────────
// Tokens
// ─────────────────────────────────────────────────────────────────────────────
export const colors = {
  bg: "#F5F2EA",
  surface: "#FFFFFF",
  border: "#E5E1D5",
  borderStrong: "#D6D1C2",
  text: "#1A1A1A",
  textMuted: "#5C5C5C",
  textSoft: "#8A8A82",

  brand: "#D04638",
  brandSoft: "#FBE8E5",
  accent: "#E08A1E",
  accentSoft: "#FBEFD9",
  info: "#2A75B0",
  infoSoft: "#E2EDF7",
  success: "#2C8D5E",
  successSoft: "#DEEEE5",

  good: "#1F8A4F",
  goodSoft: "#E2F1E8",
  bad: "#C53D2E",
  badSoft: "#FBE5E2",
  neutral: "#6B6B6B",
  neutralSoft: "#ECEAE2",

  podium: {
    gold: "#D4A017",
    goldSoft: "#FBF1D2",
    silver: "#9CA3AF",
    silverSoft: "#EEF1F4",
    bronze: "#B87333",
    bronzeSoft: "#F6E4D0",
  },
};

export const fonts = {
  body: "'Geist', -apple-system, sans-serif",
  mono: "'Geist Mono', monospace",
};

export const globalCSS = `
  @import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700;800;900&family=Geist+Mono:wght@400;500;600&display=swap');

  *, *::before, *::after { box-sizing: border-box; }

  @keyframes prod-rise {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  @keyframes prod-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(208, 70, 56, 0.55); }
    70%  { box-shadow: 0 0 0 8px rgba(208, 70, 56, 0); }
    100% { box-shadow: 0 0 0 0 rgba(208, 70, 56, 0); }
  }
`;

// ─────────────────────────────────────────────────────────────────────────────
// Page wrapper
// ─────────────────────────────────────────────────────────────────────────────
export const page = {
  background: colors.bg,
  minHeight: "100vh",
  padding: "24px 32px 28px",
  fontFamily: fonts.body,
  color: colors.text,
  fontFeatureSettings: "'tnum' 1, 'cv11' 1, 'ss01' 1",
  WebkitFontSmoothing: "antialiased",
};

export const dash = {
  display: "flex",
  flexDirection: "column",
  gap: 18,
  maxWidth: 1920,
  margin: "0 auto",
};

// ─────────────────────────────────────────────────────────────────────────────
// Header
// ─────────────────────────────────────────────────────────────────────────────
export const header = {
  wrap: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    paddingBottom: 2,
    flexWrap: "wrap",
    gap: 16,
  },
  title: {
    fontSize: 38,
    fontWeight: 800,
    letterSpacing: "-0.025em",
    lineHeight: 1,
    margin: 0,
  },
  subtitle: {
    fontSize: 15,
    color: colors.textMuted,
    letterSpacing: "0.01em",
    marginTop: 8,
  },
  right: {
    display: "flex",
    alignItems: "center",
    gap: 10,
  },
  pill: {
    display: "inline-flex",
    alignItems: "center",
    gap: 9,
    padding: "8px 16px",
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.08em",
    color: colors.textMuted,
    userSelect: "none",
  },
  pillDot: {
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: colors.success,
  },
  button: (variant) => {
    const map = {
      primary: { bg: colors.brand, color: "#fff", border: "none" },
      ghost: {
        bg: "transparent",
        color: colors.textMuted,
        border: `1px solid ${colors.border}`,
      },
    };
    const c = map[variant] || map.primary;
    return {
      background: c.bg,
      color: c.color,
      border: c.border,
      borderRadius: 10,
      padding: "10px 18px",
      fontFamily: fonts.body,
      fontSize: 12.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      cursor: "pointer",
      height: 40,
      whiteSpace: "nowrap",
    };
  },
  iconButton: (active) => ({
    background: active ? colors.text : colors.surface,
    color: active ? colors.surface : colors.textMuted,
    border: `1px solid ${active ? colors.text : colors.border}`,
    borderRadius: 10,
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    padding: 0,
    transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease",
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Upload (estado inicial)
// ─────────────────────────────────────────────────────────────────────────────
export const upload = {
  wrap: (drag) => ({
    background: drag ? colors.brandSoft : colors.surface,
    border: `2px dashed ${drag ? colors.brand : colors.borderStrong}`,
    borderRadius: 18,
    padding: "60px 32px",
    textAlign: "center",
    cursor: "pointer",
    transition: "background 0.15s ease, border-color 0.15s ease",
  }),
  icon: {
    fontSize: 56,
    color: colors.brand,
    lineHeight: 1,
    marginBottom: 16,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    letterSpacing: "-0.01em",
    marginBottom: 8,
    color: colors.text,
  },
  subtitle: {
    fontSize: 14,
    color: colors.textMuted,
    marginBottom: 24,
  },
  cta: {
    display: "inline-block",
    background: colors.brand,
    color: "#fff",
    borderRadius: 10,
    padding: "12px 24px",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    cursor: "pointer",
  },
  formats: {
    fontSize: 12,
    color: colors.textSoft,
    marginTop: 16,
    fontFamily: fonts.mono,
  },
  error: {
    background: colors.badSoft,
    border: `1px solid ${colors.bad}`,
    borderRadius: 12,
    padding: "12px 18px",
    color: colors.bad,
    fontSize: 14,
    fontWeight: 600,
    marginTop: 14,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Meta (Razão social, CNPJ, período, etc.)
// ─────────────────────────────────────────────────────────────────────────────
export const meta = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "18px 22px 20px",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 18,
  },
  item: {
    borderLeft: `3px solid ${colors.borderStrong}`,
    padding: "4px 0 4px 14px",
  },
  label: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: colors.textSoft,
    textTransform: "uppercase",
    marginBottom: 4,
  },
  value: {
    fontSize: 14,
    color: colors.text,
    fontWeight: 600,
    lineHeight: 1.4,
    wordBreak: "break-word",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// KPIs
// ─────────────────────────────────────────────────────────────────────────────
export const kpis = {
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 14,
  },
  card: (i) => {
    const accents = [
      colors.brand,
      colors.accent,
      colors.info,
      colors.success,
      colors.neutral,
      colors.brand,
    ];
    return {
      background: colors.surface,
      border: `1px solid ${colors.border}`,
      borderLeft: `4px solid ${accents[i % accents.length]}`,
      borderRadius: 14,
      padding: "16px 18px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
      animation: "prod-rise 0.32s ease",
    };
  },
  label: {
    fontSize: 11.5,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: colors.textSoft,
    textTransform: "uppercase",
  },
  value: {
    fontSize: 28,
    fontWeight: 700,
    fontVariantNumeric: "tabular-nums",
    letterSpacing: "-0.025em",
    lineHeight: 1.05,
    color: colors.text,
  },
  unit: {
    fontSize: 14,
    fontWeight: 500,
    color: colors.textMuted,
    marginLeft: 4,
  },
  hint: {
    fontSize: 13,
    color: colors.textMuted,
    fontWeight: 500,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Pódio
// ─────────────────────────────────────────────────────────────────────────────
export const podium = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "22px 28px 28px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: colors.textMuted,
  },
  empty: {
    padding: "60px 0",
    textAlign: "center",
    color: colors.textMuted,
    fontSize: 15,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1.2fr 1fr",
    alignItems: "end",
    gap: 16,
  },
  spot: (place) => {
    const map = {
      1: { color: colors.podium.gold, soft: colors.podium.goldSoft, height: 240, order: 2 },
      2: { color: colors.podium.silver, soft: colors.podium.silverSoft, height: 200, order: 1 },
      3: { color: colors.podium.bronze, soft: colors.podium.bronzeSoft, height: 170, order: 3 },
    };
    const cfg = map[place];
    return {
      background: cfg.soft,
      border: `1px solid ${cfg.color}55`,
      borderTop: `4px solid ${cfg.color}`,
      borderRadius: 14,
      padding: "20px 18px 18px",
      minHeight: cfg.height,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      order: cfg.order,
      animation: "prod-rise 0.42s ease",
    };
  },
  medal: (place) => {
    const sizes = { 1: 56, 2: 46, 3: 42 };
    return {
      width: sizes[place],
      height: "auto",
      marginBottom: 10,
      filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.18))",
    };
  },
  placeTag: (place) => {
    const map = {
      1: colors.podium.gold,
      2: colors.podium.silver,
      3: colors.podium.bronze,
    };
    return {
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.16em",
      color: map[place],
      marginBottom: 6,
      textTransform: "uppercase",
    };
  },
  name: (place) => ({
    fontSize: place === 1 ? 18 : 16,
    fontWeight: 700,
    color: colors.text,
    textAlign: "center",
    lineHeight: 1.2,
    marginBottom: 4,
    wordBreak: "break-word",
  }),
  operator: {
    fontSize: 12,
    fontWeight: 600,
    color: colors.textSoft,
    fontFamily: fonts.mono,
    marginBottom: 12,
  },
  metrics: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
    width: "100%",
    paddingTop: 10,
    borderTop: `1px dashed ${colors.borderStrong}`,
  },
  metricRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    gap: 8,
  },
  metricLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.06em",
    color: colors.textMuted,
    textTransform: "uppercase",
  },
  metricValue: {
    fontSize: 14,
    fontWeight: 700,
    fontFamily: fonts.mono,
    color: colors.text,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Tabela
// ─────────────────────────────────────────────────────────────────────────────
export const tableSection = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "20px 24px 24px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  count: {
    fontSize: 13,
    color: colors.textMuted,
    fontFamily: fonts.mono,
  },
  scroll: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    fontVariantNumeric: "tabular-nums",
  },
  th: {
    textAlign: "left",
    padding: "12px 12px",
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: colors.textSoft,
    textTransform: "uppercase",
    borderBottom: `1px solid ${colors.border}`,
    background: colors.bg,
    whiteSpace: "nowrap",
  },
  thRight: {
    textAlign: "right",
  },
  tr: (i) => ({
    background: i % 2 === 0 ? colors.surface : "#FAF8F2",
  }),
  td: {
    padding: "12px 12px",
    fontSize: 13.5,
    color: colors.text,
    borderBottom: `1px solid ${colors.border}`,
    whiteSpace: "nowrap",
  },
  tdRight: {
    textAlign: "right",
    fontFamily: fonts.mono,
    fontWeight: 600,
  },
  rank: (place) => {
    const map = {
      1: { bg: colors.podium.goldSoft, color: colors.podium.gold },
      2: { bg: colors.podium.silverSoft, color: colors.podium.silver },
      3: { bg: colors.podium.bronzeSoft, color: colors.podium.bronze },
    };
    const c = map[place] || { bg: colors.neutralSoft, color: colors.textMuted };
    return {
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 32,
      height: 26,
      padding: "0 8px",
      borderRadius: 6,
      background: c.bg,
      color: c.color,
      fontSize: 13,
      fontWeight: 700,
    };
  },
  empty: {
    padding: "40px 0",
    textAlign: "center",
    color: colors.textMuted,
    fontSize: 15,
  },
  bar: {
    position: "relative",
    height: 4,
    width: 80,
    background: colors.border,
    borderRadius: 2,
    overflow: "hidden",
    display: "inline-block",
    verticalAlign: "middle",
    marginLeft: 8,
  },
  barFill: (pct) => ({
    height: "100%",
    width: `${pct}%`,
    background: `linear-gradient(90deg, ${colors.brand}, ${colors.accent})`,
    transition: "width 0.5s ease",
  }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Bloco de comparação (gráfico de barras divergentes)
// ─────────────────────────────────────────────────────────────────────────────
export const comparison = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "22px 26px 26px",
  },
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 16,
    flexWrap: "wrap",
    gap: 12,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: colors.textMuted,
    fontFamily: fonts.mono,
  },
  legend: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    fontSize: 12,
    color: colors.textMuted,
    flexWrap: "wrap",
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
  },
  legendSwatch: (bg) => ({
    width: 12,
    height: 12,
    borderRadius: 3,
    background: bg,
  }),
  totalsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))",
    gap: 12,
    marginBottom: 18,
  },
  totalCard: (state) => {
    const map = {
      up: { bg: colors.goodSoft, border: colors.good, color: colors.good },
      down: { bg: colors.badSoft, border: colors.bad, color: colors.bad },
      flat: { bg: colors.neutralSoft, border: colors.borderStrong, color: colors.neutral },
    };
    const c = map[state] || map.flat;
    return {
      background: c.bg,
      border: `1px solid ${c.border}55`,
      borderLeft: `3px solid ${c.border}`,
      borderRadius: 12,
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 4,
    };
  },
  totalLabel: {
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.08em",
    color: colors.textSoft,
    textTransform: "uppercase",
  },
  totalValue: (state) => {
    const map = {
      up: colors.good,
      down: colors.bad,
      flat: colors.neutral,
    };
    return {
      fontSize: 20,
      fontWeight: 700,
      fontFamily: fonts.mono,
      color: map[state] || colors.text,
      letterSpacing: "-0.01em",
    };
  },
  totalHint: {
    fontSize: 12,
    color: colors.textSoft,
    fontFamily: fonts.mono,
  },
  tabs: {
    display: "flex",
    gap: 6,
    flexWrap: "wrap",
    marginBottom: 18,
    paddingBottom: 14,
    borderBottom: `1px solid ${colors.border}`,
  },
  tab: (active) => ({
    background: active ? colors.text : "transparent",
    color: active ? colors.surface : colors.textMuted,
    border: `1px solid ${active ? colors.text : colors.border}`,
    borderRadius: 999,
    padding: "7px 14px",
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    cursor: "pointer",
    transition: "background 0.15s ease, color 0.15s ease",
  }),
  chart: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  row: {
    display: "grid",
    gridTemplateColumns: "minmax(180px, 1.2fr) 2.5fr minmax(120px, 0.9fr)",
    alignItems: "center",
    gap: 14,
    padding: "6px 0",
  },
  rowName: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.text,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  rowNumero: {
    fontSize: 12,
    color: colors.textSoft,
    fontFamily: fonts.mono,
    marginLeft: 6,
  },
  barTrack: {
    position: "relative",
    display: "flex",
    height: 22,
  },
  barHalf: (side) => ({
    flex: 1,
    display: "flex",
    alignItems: "stretch",
    background: colors.bg,
    borderRadius: side === "left" ? "6px 0 0 6px" : "0 6px 6px 0",
    overflow: "hidden",
    justifyContent: side === "left" ? "flex-end" : "flex-start",
  }),
  barAxis: {
    width: 1,
    background: colors.borderStrong,
    alignSelf: "stretch",
  },
  barFill: (positive, pct) => ({
    width: `${pct}%`,
    background: positive
      ? `linear-gradient(90deg, ${colors.success}aa, ${colors.success})`
      : `linear-gradient(270deg, ${colors.brand}aa, ${colors.brand})`,
    transition: "width 0.4s ease",
  }),
  rowDelta: (state) => {
    const map = {
      up: colors.good,
      down: colors.bad,
      flat: colors.neutral,
    };
    return {
      textAlign: "right",
      fontFamily: fonts.mono,
      fontSize: 13,
      fontWeight: 700,
      color: map[state] || colors.text,
    };
  },
  empty: {
    padding: "30px 0",
    textAlign: "center",
    color: colors.textMuted,
    fontSize: 14,
  },
  filterBlock: {
    marginBottom: 14,
    paddingBottom: 14,
    borderBottom: `1px solid ${colors.border}`,
  },
  filterHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 12,
    marginBottom: 12,
  },
  filterTitleGroup: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  filterTitle: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.1em",
    color: colors.textSoft,
    textTransform: "uppercase",
  },
  filterStatus: {
    fontSize: 12.5,
    color: colors.textMuted,
    fontFamily: fonts.mono,
  },
  filterStatusActive: {
    fontSize: 12.5,
    color: colors.brand,
    fontWeight: 600,
    fontFamily: fonts.mono,
  },
  filterControls: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  searchInput: {
    flex: "1 1 220px",
    minWidth: 200,
    padding: "8px 12px",
    fontSize: 13,
    fontFamily: fonts.body,
    color: colors.text,
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 8,
    outline: "none",
  },
  clearBtn: {
    background: "transparent",
    border: `1px solid ${colors.border}`,
    color: colors.textMuted,
    borderRadius: 8,
    padding: "8px 12px",
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: "0.04em",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  chipList: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    maxHeight: 140,
    overflowY: "auto",
  },
  chip: (selected) => ({
    background: selected ? colors.text : colors.bg,
    color: selected ? colors.surface : colors.textMuted,
    border: `1px solid ${selected ? colors.text : colors.border}`,
    borderRadius: 999,
    padding: "5px 11px",
    fontSize: 12,
    fontWeight: selected ? 600 : 500,
    cursor: "pointer",
    transition: "background 0.15s ease, color 0.15s ease, border-color 0.15s ease",
    fontFamily: fonts.body,
    letterSpacing: "0.01em",
    whiteSpace: "nowrap",
  }),
  chipNumero: {
    opacity: 0.6,
    marginLeft: 6,
    fontFamily: fonts.mono,
    fontSize: 11,
  },
  chipEmpty: {
    fontSize: 12,
    color: colors.textSoft,
    fontStyle: "italic",
    padding: "6px 0",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Upload de comparação inline (banner antes do bloco de comparação)
// ─────────────────────────────────────────────────────────────────────────────
export const compareUpload = {
  wrap: (drag, error) => ({
    background: drag ? colors.brandSoft : colors.surface,
    border: `2px dashed ${error ? colors.bad : drag ? colors.brand : colors.borderStrong}`,
    borderRadius: 14,
    padding: "20px 24px",
    cursor: "pointer",
    transition: "background 0.15s ease, border-color 0.15s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    flexWrap: "wrap",
  }),
  text: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  title: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.text,
    letterSpacing: "0.02em",
  },
  subtitle: {
    fontSize: 12.5,
    color: colors.textMuted,
  },
  cta: {
    background: colors.text,
    color: colors.surface,
    border: "none",
    borderRadius: 10,
    padding: "10px 18px",
    fontFamily: fonts.body,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    cursor: "pointer",
  },
  error: {
    width: "100%",
    fontSize: 13,
    color: colors.bad,
    fontWeight: 600,
    marginTop: 6,
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Sumário lateral
// ─────────────────────────────────────────────────────────────────────────────
export const summary = {
  wrap: {
    background: colors.surface,
    border: `1px solid ${colors.border}`,
    borderRadius: 18,
    padding: "18px 22px 20px",
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: "0.14em",
    color: colors.text,
    margin: "0 0 14px",
  },
  list: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 14,
  },
  item: {
    borderLeft: `3px solid ${colors.borderStrong}`,
    padding: "4px 0 4px 14px",
  },
  itemLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.06em",
    color: colors.text,
    marginBottom: 4,
    textTransform: "uppercase",
  },
  itemValue: {
    fontSize: 14,
    color: colors.textMuted,
    lineHeight: 1.4,
  },
};
